import React, { useCallback, useState } from "react";

import AuditRestoreConfirmationForm from "../../components/forms/audit/AuditRestoreConfirmationForm";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";
import { SnackbarData, SnackbarType } from "../../components/Snackbar";
import { generateUniqueNumber } from "../../shared/utility";
import { makePostApiCall } from "../../shared/makeApiCall";
import { restoreFromAudit } from "./auditApi";

export function useAuditHistoryViewModel(
  logout,
  entityId,
  entityType,
  addSnackbar,
) {
  const [audits, setAudits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [restoringAuditId, setRestoringAuditId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });

  const showErrorModal = useCallback((titleOrSubtitle, subtitleMaybe) => {
    const title = subtitleMaybe !== undefined ? titleOrSubtitle : "Error";
    const subtitle =
      subtitleMaybe !== undefined ? subtitleMaybe : titleOrSubtitle;
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title={title}
        />
      ),
      open: true,
    });
  }, []);

  const showToast = useCallback(
    (message, type = SnackbarType.Success) => {
      if (!addSnackbar) return;
      addSnackbar(new SnackbarData(generateUniqueNumber(), message, type));
    },
    [addSnackbar],
  );

  const fetchPage = useCallback(
    async (page, append = false) => {
      if (page === 1) setLoading(true);
      else setLoadingMore(true);

      const callResult = entityId
        ? await makePostApiCall("fetchAuditsForEntity", {
            entity_id: entityId,
            entity_type: entityType,
            ...(entityType === "ORDER" ? { include_related_pricing: true } : {}),
            page,
          })
        : await makePostApiCall("fetchAuditsForEntityType", {
            entity_type: entityType,
            page,
          });

      if (page === 1) setLoading(false);
      else setLoadingMore(false);

      if (callResult.success) {
        const payload = callResult.data;
        const newAudits = payload.data ?? [];
        setAudits(append ? (prev) => [...prev, ...newAudits] : newAudits);
        setLastPage(payload.last_page ?? 1);
        setCurrentPage(page);
      } else if (callResult.errorStatus === 401) {
        logout();
      } else {
        const title =
          page === 1
            ? "Could not load audit history"
            : "Could not load more audit history";
        const message = callResult.data?.message ?? "Please try again.";
        showErrorModal(title, message);
      }
    },
    [entityId, entityType, logout, showErrorModal],
  );

  const getAudits = useCallback(() => fetchPage(1, false), [fetchPage]);

  const loadMore = useCallback(() => {
    if (currentPage < lastPage && !loadingMore) {
      fetchPage(currentPage + 1, true);
    }
  }, [currentPage, lastPage, loadingMore, fetchPage]);

  const confirmRestore = useCallback(
    async (auditId) => {
      setEmptyModelOpen({ open: false, childComponent: null });
      setRestoringAuditId(auditId);

      const callResult = await restoreFromAudit(auditId);

      setRestoringAuditId(null);

      if (callResult.success) {
        setEmptyModelOpen({ open: false, childComponent: null });
        const data = callResult.data;
        showToast(data.message ?? "Record restored successfully.");
        if (data.warnings?.length) {
          showToast(data.warnings.join(" "), SnackbarType.Error);
        }
        getAudits();
        return;
      }

      if (callResult.errorStatus === 401) {
        setEmptyModelOpen({ open: false, childComponent: null });
        logout();
        return;
      }

      const message =
        callResult.data?.message ?? "Could not restore this audit record.";

      if (callResult.errorStatus === 403) {
        setEmptyModelOpen({ open: false, childComponent: null });
        showToast(message, SnackbarType.Error);
        return;
      }

      setEmptyModelOpen({ open: false, childComponent: null });
      showErrorModal("Could not restore", message);
    },
    [getAudits, logout, showErrorModal, showToast],
  );

  const requestRestore = useCallback(
    (event) => {
      const action = event.metadata?.action;
      const isUpdated = action === "updated";

      setEmptyModelOpen({
        open: true,
        childComponent: (
          <AuditRestoreConfirmationForm
            title={
              isUpdated
                ? "Revert changes from this update?"
                : "Restore this deleted record?"
            }
            message={
              isUpdated
                ? "This will roll back to their previous values."
                : "This will bring back the deleted record."
            }
            confirmLabel={isUpdated ? "Revert" : "Restore"}
            confirmAction={() => confirmRestore(event.id)}
            setOpen={setEmptyModelOpen}
          />
        ),
      });
    },
    [confirmRestore],
  );

  return {
    audits,
    emptyModal,
    getAudits,
    hasMore: currentPage < lastPage,
    loadMore,
    loading,
    loadingMore,
    restoringAuditId,
    requestRestore,
    setEmptyModelOpen,
  };
}
